import api from './api';

// Enhanced sort options including gift score
const SORT_OPTIONS = {
    relevance: null, // Default sort
    gift_score: '-gift_score', // Gift success rate (high to low)
    gift_score_asc: 'gift_score', // Gift success rate (low to high)
    price_low: 'retail_price',
    price_high: '-retail_price',
    newest: '-created_at',
    oldest: 'created_at',
    name_asc: 'name',
    name_desc: '-name',
    popularity: '-view_count' // If available
};

export const productsService = {
    // Get all products with filtering and pagination
    async getProducts(params = {}) {
        try {
            // Enhanced parameters processing
            const processedParams = this.processParams(params);
            const response = await api.get('/products/', { params: processedParams });
            return response.data;
        } catch (error) {
            console.error('Error fetching products:', error);
            throw error;
        }
    },

    // Get single product details
    async getProduct(id) {
        try {
            const response = await api.get(`/products/${id}/`);
            return response.data;
        } catch (error) {
            console.error('Error fetching product:', error);
            throw error;
        }
    },

    // Enhanced search products using semantic search with gift features
    async searchProducts(query, filters = {}) {
        try {
            const processedParams = this.processSearchParams({ q: query, ...filters });
            const response = await api.get('/products/search/', { params: processedParams });
            return response.data;
        } catch (error) {
            console.error('Error searching products:', error);
            throw error;
        }
    },

    // Get products with gift-specific filtering (no search query)
    async filterProducts(filters = {}) {
        try {
            const processedParams = this.processParams(filters);
            const response = await api.get('/products/', { params: processedParams });
            return response.data;
        } catch (error) {
            console.error('Error filtering products:', error);
            throw error;
        }
    },

    // Get related products
    async getRelatedProducts(productId, limit = 6) {
        try {
            const params = limit ? { limit } : {};
            const response = await api.get(`/products/${productId}/related/`, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching related products:', error);
            throw error;
        }
    },

    // Get similar gifts based on gift characteristics
    async getSimilarGifts(productId, filters = {}, limit = 6) {
        try {
            const params = {
                similar_to: productId,
                limit,
                ...this.processParams(filters)
            };
            const response = await api.get('/products/similar-gifts/', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching similar gifts:', error);
            throw error;
        }
    },

    // Get product categories
    async getCategories() {
        try {
            const response = await api.get('/products/categories/');
            return response.data;
        } catch (error) {
            console.error('Error fetching categories:', error);
            throw error;
        }
    },

    // Get category details
    async getCategory(id) {
        try {
            const response = await api.get(`/products/categories/${id}/`);
            return response.data;
        } catch (error) {
            console.error('Error fetching category:', error);
            throw error;
        }
    },

    // Get occasions for filtering
    async getOccasions() {
        try {
            const response = await api.get('/occasions/');
            return response.data;
        } catch (error) {
            console.error('Error fetching occasions:', error);
            throw error;
        }
    },

    // Record user product view
    async recordProductView(productId) {
        try {
            const response = await api.post('/products/user-views/', {
                product: productId
            });
            return response.data;
        } catch (error) {
            console.error('Error recording product view:', error);
            throw error;
        }
    },

    // Get recently viewed products
    async getRecentlyViewed(limit = 10, days = null) {
        try {
            const params = { limit };
            if (days) params.days = days;

            const response = await api.get('/products/user-views/recently_viewed/', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching recently viewed products:', error);
            throw error;
        }
    },

    // Get gift recommendations based on relationship and occasion
    async getGiftRecommendations(relationship = null, occasion = null, ageRange = null, gender = null, limit = 20) {
        try {
            const params = { limit };
            if (relationship) params.relationship = relationship;
            if (occasion) params.occasion = occasion;
            if (ageRange) params.age_range = ageRange;
            if (gender) params.gender_suitability = gender;

            const response = await api.get('/products/gift-recommendations/', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching gift recommendations:', error);
            throw error;
        }
    },

    // Get trending gifts
    async getTrendingGifts(timeframe = 'week', limit = 20) {
        try {
            const params = { timeframe, limit };
            const response = await api.get('/products/trending-gifts/', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching trending gifts:', error);
            throw error;
        }
    },

    // Enhanced parameter processing for gift-specific features
    processParams(params) {
        const processed = { ...params };

        // Handle sorting
        if (processed.sort && SORT_OPTIONS[processed.sort]) {
            processed.ordering = SORT_OPTIONS[processed.sort];
            delete processed.sort;
        }

        // Handle interests (join arrays to comma-separated strings)
        if (processed.interests && Array.isArray(processed.interests)) {
            processed.interests = processed.interests.join(',');
        }
        if (processed.negativeInterests && Array.isArray(processed.negativeInterests)) {
            processed.negative_interests = processed.negativeInterests.join(',');
            delete processed.negativeInterests;
        }

        // Handle occasions
        if (processed.occasions && Array.isArray(processed.occasions)) {
            processed.occasions = processed.occasions.join(',');
        }

        // Handle categories
        if (processed.categories && Array.isArray(processed.categories)) {
            processed.categories = processed.categories.join(',');
        }

        // Handle price range
        if (processed.priceRange && typeof processed.priceRange === 'object') {
            if (processed.priceRange.min) processed.min_price = processed.priceRange.min;
            if (processed.priceRange.max) processed.max_price = processed.priceRange.max;
            delete processed.priceRange;
        }

        return processed;
    },

    // Enhanced search parameter processing
    processSearchParams(params) {
        const processed = this.processParams(params);

        // Ensure we're using the search endpoint specific parameters
        if (processed.gender_suitability && !processed.gender) {
            processed.gender = processed.gender_suitability;
        }

        return processed;
    },

    // Get available sort options
    getSortOptions() {
        return [
            { value: 'relevance', label: 'Most Relevant', default: true },
            { value: 'gift_score', label: 'Gift Success Rate', icon: '✓' },
            { value: 'price_low', label: 'Price: Low to High', icon: '💰' },
            { value: 'price_high', label: 'Price: High to Low', icon: '💰' },
            { value: 'newest', label: 'Newest First', icon: '🆕' },
            { value: 'name_asc', label: 'Name: A-Z', icon: '🔤' },
        ];
    },

    // Get available age ranges
    getAgeRanges() {
        return [
            { value: '0-2', label: 'Baby (0-2)' },
            { value: '3-7', label: 'Child (3-7)' },
            { value: '8-12', label: 'Kid (8-12)' },
            { value: '13-17', label: 'Teen (13-17)' },
            { value: '18-25', label: 'Young Adult (18-25)' },
            { value: '25-35', label: 'Adult (25-35)' },
            { value: '35-45', label: 'Mid Adult (35-45)' },
            { value: '45-55', label: 'Mature Adult (45-55)' },
            { value: '55+', label: 'Senior (55+)' },
            { value: '18+', label: 'Adults (18+)' }
        ];
    },

    // Get available gender options
    getGenderOptions() {
        return [
            { value: 'M', label: 'Men', icon: '👨' },
            { value: 'F', label: 'Women', icon: '👩' },
            { value: 'U', label: 'Unisex', icon: '👤' }
        ];
    },

    // Validate gift score (should be 0-1)
    validateGiftScore(score) {
        return typeof score === 'number' && score >= 0 && score <= 1;
    },

    // Convert gift score to percentage
    giftScoreToPercentage(score) {
        if (!this.validateGiftScore(score)) return 0;
        return Math.round(score * 100);
    },

    // Get gift score display color
    getGiftScoreColor(score) {
        const percentage = this.giftScoreToPercentage(score);
        if (percentage >= 90) return 'excellent';
        if (percentage >= 75) return 'good';
        if (percentage >= 60) return 'fair';
        return 'low';
    }
};

export default productsService; 